import { gql } from '@apollo/client';
import { apolloClient } from '../graphql/client';
import { RoleInput } from '../types';

class RoleService {
  // Get all roles
  async getRoles() {
    const query = gql`
      query GetRoles {
        roles {
          id
          roleId
          name
          description
          isActive
          createdDate
        }
      }
    `;

    try {
      console.log('Attempting to query GraphQL for roles at:', process.env.REACT_APP_GRAPHQL_ENDPOINT);
      const result = await apolloClient.query({
        query,
        fetchPolicy: 'network-only'
      });
      console.log('GraphQL roles response:', result);
      return result.data.roles;
    } catch (error: any) {
      console.error('Error fetching roles:', error);
      console.error('Network error:', error.networkError);
      console.error('GraphQL errors:', error.graphQLErrors);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to fetch roles: ${error.message}`);
      }
    }
  }

  // Get role by ID
  async getRoleById(id: string) {
    const query = gql`
      query GetRole($id: String!) {
        role(id: $id) {
          id
          roleId
          name
          description
          isActive
          createdDate
        }
      }
    `;

    try {
      const result = await apolloClient.query({
        query,
        variables: { id },
        fetchPolicy: 'network-only'
      });
      return result.data.role;
    } catch (error: any) {
      console.error('Error fetching role by ID:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to fetch role: ${error.message}`);
      }
    }
  }

  // Create role
  async createRole(input: RoleInput) {
    const mutation = gql`
      mutation CreateRole($input: RoleInput!) {
        createRole(input: $input) {
          id
          roleId
          name
          description
          isActive
          createdDate
        }
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { input }
      });
      return result.data.createRole;
    } catch (error: any) {
      console.error('Error creating role:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to create role: ${error.message}`);
      }
    }
  }

  // Update role
  async updateRole(id: string, input: RoleInput) {
    const mutation = gql`
      mutation UpdateRole($id: String!, $input: RoleInput!) {
        updateRole(id: $id, input: $input) {
          id
          roleId
          name
          description
          isActive
          createdDate
        }
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { id, input }
      });
      return result.data.updateRole;
    } catch (error: any) {
      console.error('Error updating role:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to update role: ${error.message}`);
      }
    }
  }

  // Delete role
  async deleteRole(id: string) {
    const mutation = gql`
      mutation DeleteRole($id: String!) {
        deleteRole(id: $id)
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { id }
      });
      return result.data.deleteRole;
    } catch (error: any) {
      console.error('Error deleting role:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to delete role: ${error.message}`);
      }
    }
  }
}

const roleService = new RoleService();
export default roleService;
